import { useEffect, useRef, useState } from 'react'
// Imported (not referenced as a `public/` path) so Vite fingerprints each into
// `/assets/backdrop-N-<size>-<hash>.avif` — see the font note in app.css (VIT-17753).
// Three responsive tiers per backdrop (mobile/laptop/desktop — see the <picture>
// and scripts/resize-backdrops.sh), AVIF only (no WebP fallback: the backdrop is
// decorative, so the handful of Safari <16.4 / iOS <16.4 viewers simply see the
// dark overlay instead).
import backdrop1Desktop from '../images/backdrop-1-desktop.avif'
import backdrop1Laptop from '../images/backdrop-1-laptop.avif'
import backdrop1Mobile from '../images/backdrop-1-mobile.avif'
import backdrop2Desktop from '../images/backdrop-2-desktop.avif'
import backdrop2Laptop from '../images/backdrop-2-laptop.avif'
import backdrop2Mobile from '../images/backdrop-2-mobile.avif'
import backdrop3Desktop from '../images/backdrop-3-desktop.avif'
import backdrop3Laptop from '../images/backdrop-3-laptop.avif'
import backdrop3Mobile from '../images/backdrop-3-mobile.avif'
import backdrop4Desktop from '../images/backdrop-4-desktop.avif'
import backdrop4Laptop from '../images/backdrop-4-laptop.avif'
import backdrop4Mobile from '../images/backdrop-4-mobile.avif'
import backdrop5Desktop from '../images/backdrop-5-desktop.avif'
import backdrop5Laptop from '../images/backdrop-5-laptop.avif'
import backdrop5Mobile from '../images/backdrop-5-mobile.avif'
import type { GeoLocation } from '../routes/resource.geo'

// The home hero's location-aware background photo, plus the pure logic that
// picks it. There are five photos, one per broad US region; `geo.region` (the
// 2-letter subdivision code CloudFront supplies — see resource.geo.ts) maps to
// one of them. Non-US or unresolved geo falls back to DEFAULT_BACKDROP.
//
// We deliberately key off the state code rather than lat/lng: CloudFront gives
// us the region for free on every viewer, and it's all a region-granularity
// backdrop needs. If we ever want finer placement we'd have to introduce a
// geo-IP lat/lng source, which this intentionally avoids.

export type BackdropId = 1 | 2 | 3 | 4 | 5

// Shown to non-US viewers and during the pre-hydration / geo-loading window,
// when `geo.region` isn't known yet (the landing HTML is shared-cached with no
// location in the cache key, so geo resolves client-side after hydration — see
// use-geo.ts). Region 1 (Northwest) is an arbitrary but stable choice.
export const DEFAULT_BACKDROP: BackdropId = 1

// State/DC → backdrop, grouped into five broad regions. Covers all 50 states
// plus DC (51 entries; hero-backdrop.unit.test.ts asserts completeness so a
// missing state can't slip through to the fallback unnoticed). Keys are
// upper-case 2-letter codes to match the normalisation in backdropForGeo.
const BACKDROP_BY_STATE: Record<string, BackdropId> = {
  // 1 — Northwest / Mountain
  WA: 1,
  OR: 1,
  ID: 1,
  MT: 1,
  WY: 1,
  AK: 1,
  // 2 — Southwest
  CA: 2,
  NV: 2,
  UT: 2,
  AZ: 2,
  NM: 2,
  CO: 2,
  HI: 2,
  // 3 — Midwest / Central
  ND: 3,
  SD: 3,
  NE: 3,
  KS: 3,
  MN: 3,
  IA: 3,
  MO: 3,
  WI: 3,
  IL: 3,
  IN: 3,
  MI: 3,
  OH: 3,
  // 4 — Northeast
  NY: 4,
  NJ: 4,
  PA: 4,
  CT: 4,
  RI: 4,
  MA: 4,
  VT: 4,
  NH: 4,
  ME: 4,
  MD: 4,
  DE: 4,
  DC: 4,
  WV: 4,
  VA: 4,
  // 5 — Southeast / South
  TX: 5,
  OK: 5,
  AR: 5,
  LA: 5,
  MS: 5,
  AL: 5,
  TN: 5,
  KY: 5,
  GA: 5,
  FL: 5,
  SC: 5,
  NC: 5,
}

// Exported for the completeness test — the canonical set of states we map.
export const MAPPED_STATES: readonly string[] = Object.keys(BACKDROP_BY_STATE)

export const backdropForGeo = (geo: GeoLocation | undefined): BackdropId => {
  // Only US viewers have a state we recognise; everyone else gets the default.
  if (geo?.country?.trim().toUpperCase() !== 'US') return DEFAULT_BACKDROP
  const region = geo.region?.trim().toUpperCase()
  if (region == null || region === '') return DEFAULT_BACKDROP
  // An unrecognised code (a US territory like PR, or a future addition) also
  // falls back rather than throwing.
  return BACKDROP_BY_STATE[region] ?? DEFAULT_BACKDROP
}

type BackdropSize = 'desktop' | 'laptop' | 'mobile'

const BACKDROP_SRC: Record<BackdropId, Record<BackdropSize, string>> = {
  1: { desktop: backdrop1Desktop, laptop: backdrop1Laptop, mobile: backdrop1Mobile },
  2: { desktop: backdrop2Desktop, laptop: backdrop2Laptop, mobile: backdrop2Mobile },
  3: { desktop: backdrop3Desktop, laptop: backdrop3Laptop, mobile: backdrop3Mobile },
  4: { desktop: backdrop4Desktop, laptop: backdrop4Laptop, mobile: backdrop4Mobile },
  5: { desktop: backdrop5Desktop, laptop: backdrop5Laptop, mobile: backdrop5Mobile },
}

export const backdropSrc = (id: BackdropId, size: BackdropSize): string => BACKDROP_SRC[id][size]

// One region's backdrop photo, keyed by id so it remounts (and its fade resets)
// whenever the region changes. Three responsive AVIF tiers, picked by viewport
// width: mobile ≤640px (Tailwind `sm`), laptop ≤1280px (`xl`), desktop above.
// Each asset is ~2× its breakpoint so it stays crisp at retina DPR — see
// scripts/resize-backdrops.sh.
//
// Starts at opacity-0 and fades in on load so the default → region swap eases in
// rather than hard-cutting. The fade is a full second (longer than a typical
// UI transition) specifically to smooth over that swap: the region image
// loads a few hundred ms after first paint, and a slower fade reads as an
// intentional reveal rather than a flash. Under motion-reduce the fade is
// dropped (instant). A cached image can already be complete before React
// attaches `onLoad`, so a mount effect also checks `img.complete`; the load
// handler still drives the animated case.
const BackdropImage = ({ id }: { id: BackdropId }) => {
  const [loaded, setLoaded] = useState(false)
  const imageRef = useRef<HTMLImageElement>(null)

  useEffect(() => {
    if (imageRef.current?.complete) {
      setLoaded(true)
    }
  }, [])

  return (
    <picture>
      {/* First matching media wins, so order narrowest → widest. The <img> src
          is the desktop tier, used above 1280px and as the universal fallback. */}
      <source media="(max-width: 640px)" srcSet={backdropSrc(id, 'mobile')} type="image/avif" />
      <source media="(max-width: 1280px)" srcSet={backdropSrc(id, 'laptop')} type="image/avif" />
      <img
        ref={imageRef}
        src={backdropSrc(id, 'desktop')}
        alt=""
        data-testid="hero-backdrop"
        data-backdrop-id={id}
        // The LCP element is the hero <h1> text, NOT this photo: the image renders
        // at opacity-0 and only fades in after load (see className below), so it's
        // never the largest *contentful* paint. That's deliberate — the <h1> paints
        // immediately in the preloaded Space Grotesk, and this ~60 KB decorative
        // image, behind a ~90%-opaque overlay, must not gate LCP.
        //   - fetchPriority="low": stop the preload scanner racing this photo against
        //     the LCP-critical font/JS on a constrained connection. It's decorative,
        //     so a slightly later fade-in is invisible under the overlay.
        //   - decoding="async": keep the decode off the main thread.
        // If you ever make this image visible on first paint, it WILL become the LCP
        // element and must be preloaded (and this deprioritisation reconsidered).
        fetchPriority="low"
        decoding="async"
        onLoad={() => setLoaded(true)}
        className={`absolute size-full inset-0 object-cover object-top-right saturate-50 brightness-80 contrast-125 motion-safe:transition-opacity motion-safe:duration-1000 ${
          loaded ? 'opacity-100' : 'opacity-0 motion-reduce:opacity-100'
        }`}
      />
    </picture>
  )
}

// The home hero's location-aware background photo. Picks one of five regional
// backdrops from the viewer's state (backdropForGeo).
//
// Geo resolves client-side after hydration, so the first render uses the default
// backdrop and this swaps to the regional one once geo arrives — see the fade in
// BackdropImage. The heavy overlay in the hero (a ~90%-opaque panel plus
// saturate/brightness filters) means the photo is barely visible, so the brief
// pre-load moment just reads as the dark hero — no jarring flash.
export const HeroBackdrop = ({ geo }: { geo: GeoLocation | undefined }) => {
  const id = backdropForGeo(geo)
  return <BackdropImage key={id} id={id} />
}
